import React from "react";
import { SegmentView } from "@tiny/api";
import {
  SimpleListItem,
  IconButton,
  Typography,
  Chip,
  TruncateTooltip,
  Checkbox,
} from "@tiny/components";
import ItemActionTimeInfo from "@tiny/components/src/ItemActionTimeInfo/ItemActionTimeInfo";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import Download from "@tiny/icons/generated/Download";
import Upload from "@tiny/icons/generated/Upload";
import { useAudienceRow } from "./AudienceRow.hooks";
import { useStyles } from "./AudienceRow.styles";
import { TinyImporter } from "../../TinyImportDialog";
import useTinyImporter from "../../../hooks/useTinyImporter";

const dateFormat = "dd MMM, yy - hh:mm a";

export interface AudienceRowProps {
  item: SegmentView;
  reloadAudiences?: () => void;
  closeDialog: () => void;
  onSuccess?: () => void;
  mode?: "update" | "export" | "select";
  segmentIds?: string[];

  selectMode?: boolean;
  selected?: boolean;
  onClick?: () => void;
}

function AudienceRow(props: AudienceRowProps) {
  const {
    item,
    onSuccess,
    reloadAudiences,
    mode = "update",
    closeDialog,
    selectMode = false,
    selected = false,
    onClick,
  } = props;

  const { lgUp } = useBreakpoints();

  const classes = useStyles();

  const { chipColor, chipText, actions, handleExportAction, exportLoading } =
    useAudienceRow(props);

  const { closeTinyImporter, openTinyImporter, tinyImporterOpen } =
    useTinyImporter();

  const handleActionClick = React.useCallback(() => {
    if (mode === "export") {
      handleExportAction();
    } else {
      openTinyImporter();
    }
  }, [mode, handleExportAction, openTinyImporter]);

  return (
    <>
      <SimpleListItem
        contentClassName={classes.root}
        onClick={selectMode ? onClick : undefined}
      >
        <div className={classes.body}>
          {lgUp ? (
            <TruncateTooltip arrow>
              <Typography
                variant="body2"
                noWrap
                className={classes.name}
                color="primary"
                tone="main"
              >
                {item.name}
              </Typography>
            </TruncateTooltip>
          ) : (
            <Typography
              variant="body2"
              className={classes.name}
              color="primary"
              tone="main"
            >
              {item.name}
            </Typography>
          )}
          <Chip label={chipText} color={chipColor} size="small" />
          <ItemActionTimeInfo
            mt={0}
            actions={actions}
            dateFormat={dateFormat}
            highlight="Created"
          />
        </div>
        <div className={classes.action}>
          {selectMode ? (
            <Checkbox checked={selected} color="primary" />
          ) : (
            <IconButton
              size="small"
              onClick={handleActionClick}
              disabled={exportLoading}
              className={classes.icon}
            >
              {mode === "export" ? <Download /> : <Upload />}
            </IconButton>
          )}
        </div>
      </SimpleListItem>

      <TinyImporter
        open={tinyImporterOpen}
        onClose={closeTinyImporter}
        value={item?.name}
        segmentDetails={item as never}
        onSuccess={() => {
          closeTinyImporter();
          closeDialog();
          onSuccess?.();
          reloadAudiences?.();
        }}
      />
    </>
  );
}

export default AudienceRow;
